import axiosInstance from "../../../../lib/axiosInstance";
import { MAX_UPLOAD_BYTES, putToS3 } from "./newTelemetry";
import { DEMO_INSIGHT, createDemoLog, demoMessages, isDemoId, readDemoLog } from "./demoLog";

// Public gallery endpoints — unauthenticated on the backend. axiosInstance still
// attaches a bearer if one happens to exist; these routes ignore it.

const detail = (error, fallback) => error?.response?.data?.detail || fallback;

export const listPublicLogs = async ({ limit = 24 } = {}) => {
  try {
    const response = await axiosInstance.get("/telemetry/public/logs", { params: { limit } });
    return response.data.logs || [];
  } catch (error) {
    throw new Error(detail(error, "Failed to load public logs"));
  }
};

export const getPublicLog = async (id) => {
  if (isDemoId(id)) {
    const log = readDemoLog(id);
    if (!log) throw new Error("This demo log expired — upload again to see the flow.");
    return log;
  }
  try {
    const response = await axiosInstance.get(`/telemetry/public/logs/${id}`);
    return response.data.log;
  } catch (error) {
    throw new Error(detail(error, "Failed to load log"));
  }
};

export const getPublicMessages = async (id, { type, fields, maxPoints } = {}) => {
  if (isDemoId(id)) return demoMessages(type, fields?.[0]);
  try {
    const response = await axiosInstance.get(`/telemetry/public/logs/${id}/messages`, {
      params: {
        type,
        fields: fields?.length ? fields.join(",") : undefined,
        max_points: maxPoints || undefined,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(detail(error, "Failed to load series"));
  }
};

// --- Anonymous upload -------------------------------------------------------
// Same presign → PUT → parse dance as the authed flow, on routes that take no
// bearer. A log uploaded this way is public by definition: there is no account
// to own it, so it lands in the gallery and stays there until someone signs in
// and claims it.

export const uploadPublicLog = async (file, { onProgress } = {}) => {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB)`);
  }
  let presigned;
  try {
    const response = await axiosInstance.post("/telemetry/public/logs/upload-url", {
      file_name: file.name,
      size_bytes: file.size,
    });
    presigned = response.data; // { log: {id, status}, upload_url }
  } catch (error) {
    // Status is carried so callers can tell "not deployed yet" (404) from a
    // real rejection and degrade instead of dead-ending the visitor.
    const failure = new Error(detail(error, "Failed to start upload"));
    failure.status = error?.response?.status;
    throw failure;
  }
  await putToS3(presigned.upload_url, file, onProgress);
  try {
    await axiosInstance.post(`/telemetry/public/logs/${presigned.log.id}/parse`);
  } catch (error) {
    throw new Error(detail(error, "Failed to start parsing"));
  }
  return presigned.log.id;
};

export const pollPublicLogUntilDone = async (id, { intervalMs = 2000, maxAttempts = 90 } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const log = await getPublicLog(id);
    if (log.status === "ready" || log.status === "failed") return log;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error("Parsing timed out");
};

// The one free AI pass. Anonymous callers get `{ preview, locked }`; the rest of
// the write-up and any follow-up question needs an account. The cap is the
// backend's to enforce — this only renders whatever it chose to hand back.
export const getPublicInsight = async (id) => {
  if (isDemoId(id)) return DEMO_INSIGHT;
  try {
    const response = await axiosInstance.get(`/telemetry/public/logs/${id}/insight`);
    return response.data; // { preview, locked, locked_sections? }
  } catch (error) {
    throw new Error(detail(error, "Failed to load insight"));
  }
};

/**
 * Anonymous upload, falling back to the simulated log while the route isn't
 * usable — a 404 (not deployed) or no response at all (unreachable host, no
 * CORS). Both mean the same thing today: there is nothing to upload to, so show
 * the flow rather than a dead end.
 *
 * A real HTTP rejection is NOT swallowed. A 403 (quota) or 413 (too large) is
 * the backend saying no, and the visitor has to see that instead of a demo that
 * pretends it worked. Drop this whole fallback with `demoLog.js`.
 */
export const uploadPublicLogOrDemo = async (file, options) => {
  try {
    return await uploadPublicLog(file, options);
  } catch (error) {
    if (error.status && ![404, 405].includes(error.status)) throw error;
    return createDemoLog(file);
  }
};
